import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from './data/api';
import { getPhase, PHASE_INFO, WORKOUTS } from './data/programData';
import BottomNav from './components/BottomNav';
import Dashboard from './pages/Dashboard';
import Workout from './pages/Workout';
import Nutrition from './pages/Nutrition';
import Progress from './pages/Progress';
import More from './pages/More';
import Onboarding from './components/Onboarding';
import Login from './components/Login';
import PlanBuilder from './components/PlanBuilder';
import RestTimer from './components/RestTimer';
import { ThemeProvider } from './components/ThemeProvider';
import AnimatedBackground from './components/AnimatedBackground';
import Toast, { useToast } from './components/Toast';
import './App.css';

function App() {
  const [tab, setTab] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [week, setWeek] = useState(1);
  const [day, setDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [authScreen, setAuthScreen] = useState(null); // 'login' | 'register' | null
  const [showPlanBuilder, setShowPlanBuilder] = useState(false);
  const [customProgram, setCustomProgram] = useState(null);
  const [restTimer, setRestTimer] = useState(null);
  const [workoutLogs, setWorkoutLogs] = useState({});
  const [mealLogs, setMealLogs] = useState({});
  const [showSplash, setShowSplash] = useState(true);
  const [splashFading, setSplashFading] = useState(false);
  const audioRef = useRef(null);
  const { toast, showToast } = useToast();

  const today = new Date().toISOString().split('T')[0];
  const phase = getPhase(week);
  const phaseInfo = PHASE_INFO[phase];

  const loadUserData = useCallback(async (u) => {
    setUser(u);
    if (u.start_date) {
      const start = new Date(u.start_date);
      const now = new Date();
      const diffDays = Math.floor((now - start) / (1000 * 60 * 60 * 24));
      setWeek(Math.min(48, Math.max(1, Math.floor(diffDays / 7) + 1)));
      setDay(Math.min(6, Math.max(1, (diffDays % 7) + 1)));
    }
    const wLogs = await api.getWorkoutLogs(u.user_id);
    const logMap = {};
    wLogs.forEach(l => { logMap[l.date] = l; });
    setWorkoutLogs(logMap);
    const mLogs = await api.getMealLogs(u.user_id);
    const mealMap = {};
    mLogs.forEach(l => { mealMap[l.date] = l; });
    setMealLogs(mealMap);
    // Load custom program if user has one
    if (u.plan_type === 'custom') {
      try {
        const cp = await api.getCustomProgram(u.user_id);
        setCustomProgram(cp.program);
      } catch (e) { /* no custom plan yet */ }
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('leprotocol_token');
      if (token) {
        try {
          const u = await api.getMe();
          await loadUserData(u);
        } catch (e) {
          // Token expired or invalid
          localStorage.removeItem('leprotocol_token');
          localStorage.removeItem('leprotocol_user');
          setAuthScreen('login');
        }
      } else {
        setAuthScreen('register');
      }
    } catch (e) {
      console.error('Load error:', e);
      setAuthScreen('register');
    } finally {
      setLoading(false);
    }
  }, [loadUserData]);

  useEffect(() => { loadData(); }, [loadData]);

  const onUserCreated = async (newUser) => {
    try {
      await loadUserData(newUser);
    } catch (e) {
      console.error('Load data after register:', e);
      setUser(newUser);
    }
    setAuthScreen(null);
    // If user chose custom path, show plan builder
    if (newUser.plan_type === 'custom') {
      setShowPlanBuilder(true);
    }
  };

  const onLoginComplete = async (loggedInUser) => {
    try {
      await loadUserData(loggedInUser);
    } catch (e) {
      console.error('Load data after login:', e);
      setUser(loggedInUser);
    }
    setAuthScreen(null);
  };

  const updateWorkoutLog = async (date, sets, duration, note) => {
    if (!user) return;
    await api.saveWorkoutLog({ user_id: user.user_id, date, sets, duration, note });
    setWorkoutLogs(prev => ({ ...prev, [date]: { date, sets, duration, note } }));
  };

  const updateMealLog = async (date, meals) => {
    if (!user) return;
    await api.saveMealLog({ user_id: user.user_id, date, meals });
    setMealLogs(prev => ({ ...prev, [date]: { date, meals } }));
  };

  const refreshUser = async () => {
    if (!user) return;
    const updated = await api.getMe();
    setUser(updated);
  };

  const logout = () => {
    localStorage.removeItem('leprotocol_token');
    localStorage.removeItem('leprotocol_user');
    setUser(null);
    setWorkoutLogs({});
    setMealLogs({});
    setAuthScreen('login');
  };

  // Splash + sound
  const playLockIn = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/lock-in.mp3');
        audioRef.current.volume = 0.8;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (e) { /* silent */ }
  };

  const enterApp = () => {
    playLockIn();
    setSplashFading(true);
    setTimeout(() => setShowSplash(false), 600);
  };

  if (showSplash) {
    return (
      <div className="h-screen flex flex-col items-center justify-center cursor-pointer select-none" style={{ backgroundColor: '#1A1A1F' }} onClick={enterApp} data-testid="splash-screen">
        <div className={`text-center transition-all duration-500 ${splashFading ? 'opacity-0 scale-110' : 'opacity-100 scale-100'}`}>
          <div className="text-impact text-8xl sm:text-9xl text-white mb-1">LE PROTOCOL</div>
          <div className="text-command text-base sm:text-lg tracking-[0.3em] mb-1" style={{ color: 'var(--color-brand)' }}>48-Week Transformation</div>
          <div className="text-data text-[10px] text-neutral-500 tracking-[0.4em] mb-16">Diamond Edition</div>
          <div className="inline-block px-10 py-4 rounded-xl glow-border animate-glow" style={{ borderColor: 'var(--theme-border)' }}>
            <span className="text-command text-sm tracking-[0.4em]" style={{ color: 'var(--color-brand)' }}>TAP TO LOCK IN</span>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ backgroundColor: '#1A1A1F' }} data-testid="loading-screen">
        <div className="text-center">
          <div className="text-impact text-5xl text-white mb-2">LE PROTOCOL</div>
          <div className="text-data text-xs text-neutral-500 animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  if (authScreen === 'register' || (!user && authScreen !== 'login')) {
    return <Onboarding onComplete={onUserCreated} onSwitchToLogin={() => setAuthScreen('login')} />;
  }

  if (authScreen === 'login' || !user) {
    return <Login onComplete={onLoginComplete} onSwitchToRegister={() => setAuthScreen('register')} />;
  }

  // Show Plan Builder for custom-path users who haven't generated yet
  if (showPlanBuilder && user) {
    return (
      <div className="bg-base min-h-screen">
        <Toast toast={toast} />
        <PlanBuilder
          user={user}
          showToast={showToast}
          onComplete={(program) => {
            setCustomProgram(program);
            setShowPlanBuilder(false);
            // Reload user to get plan_type update
            api.getMe().then(u => setUser(u)).catch(() => {});
          }}
        />
      </div>
    );
  }

  const getDateForWeekDay = (w, d) => {
    if (!user.start_date) return today;
    const start = new Date(user.start_date + 'T00:00:00');
    const offset = (w - 1) * 7 + (d - 1);
    const target = new Date(start.getTime() + offset * 24 * 60 * 60 * 1000);
    return target.toISOString().split('T')[0];
  };

  const dateForDay = getDateForWeekDay(week, day);

  // Compute workout progress for theme
  const todayLog = workoutLogs[today] || {};
  const todaySets = todayLog.sets || [];
  const todayCompleted = todaySets.length > 0 && todaySets.some(s => s.done);
  const currentDayWorkout = (() => {
    if (customProgram?.phases) {
      let ws = 0;
      for (const p of customProgram.phases) {
        if (week <= ws + (p.weeks || 12)) return p.workouts?.[String(day)] || null;
        ws += p.weeks || 12;
      }
      return customProgram.phases[0]?.workouts?.[String(day)] || null;
    }
    return WORKOUTS[phase] ? WORKOUTS[phase][day] : null;
  })();
  const totalSetsToday = currentDayWorkout?.exercises?.reduce((sum, ex) => sum + (ex.sets || 0), 0) || 1;
  const completedSetsToday = todaySets.filter(s => s.done).length;
  const workoutProgress = Math.min(100, Math.round((completedSetsToday / totalSetsToday) * 100));

  return (
    <ThemeProvider workoutProgress={workoutProgress} workoutStarted={completedSetsToday > 0} todayCompleted={workoutProgress >= 100}>
    <AnimatedBackground />
    <div className="min-h-screen font-body text-white pb-20 relative z-10" style={{ backgroundColor: 'transparent' }} data-testid="app-root">
      <Toast toast={toast} />
      {restTimer && <RestTimer seconds={restTimer} onClose={() => setRestTimer(null)} />}
      <div className="max-w-xl mx-auto">
        {tab === 'dashboard' && (
          <Dashboard user={user} week={week} day={day} phase={phase} phaseInfo={phaseInfo}
            workoutLogs={workoutLogs} mealLogs={mealLogs} today={today}
            setWeek={setWeek} setDay={setDay} getDateForWeekDay={getDateForWeekDay}
            customProgram={customProgram} />
        )}
        {tab === 'workout' && (
          <Workout user={user} week={week} day={day} setDay={setDay} phase={phase} phaseInfo={phaseInfo}
            dateForDay={dateForDay} workoutLogs={workoutLogs} updateWorkoutLog={updateWorkoutLog}
            startRestTimer={(s) => setRestTimer(s)} refreshUser={refreshUser} showToast={showToast}
            customProgram={customProgram} />
        )}
        {tab === 'nutrition' && (
          <Nutrition user={user} phase={phase} today={today} mealLogs={mealLogs} updateMealLog={updateMealLog} showToast={showToast}
            customProgram={customProgram} />
        )}
        {tab === 'progress' && (
          <Progress user={user} workoutLogs={workoutLogs} today={today} showToast={showToast} />
        )}
        {tab === 'more' && (
          <More user={user} refreshUser={refreshUser} today={today} logout={logout} showToast={showToast}
            customProgram={customProgram} onRegeneratePlan={() => setShowPlanBuilder(true)} />
        )}
      </div>
      <BottomNav tab={tab} setTab={setTab} />
    </div>
    </ThemeProvider>
  );
}

export default App;
